import { useState } from 'react';
import Linkify from 'react-linkify';
import { DnsRecord } from '@mews-commander/data-access';
import { CommanderThunkDispatch } from '@mews-commander/types';
import { Card, ContentContainer, matchDecorator } from '@mews-commander/ui';
import { Banner, Button, Link, Body, Title, Stack, Tag } from '@optimus-web/core';
import { LocalizedText, useDialogAction, useLocalizeText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { useCommanderThunkDispatch } from '@mews-commander/utils';
import { open } from '@mews-commander/chat';
import EntityDeleteDialog from '../entityManagement/components/EntityDeleteDialog';
import { DnsRecordList } from './DnsRecordList';

interface EmailSenderStatusPageProps {
    email: string;
    verified: boolean;
    verifyLoaded: boolean;
    dnsRecords: DnsRecord[];
    onVerify: () => Promise<void>;
    onDelete: () => Promise<void>;
}

export const EmailSenderStatusPage = ({
    email,
    verified,
    dnsRecords,
    verifyLoaded,
    onVerify,
    onDelete,
}: EmailSenderStatusPageProps) => {
    const deleteAction = useDialogAction(onDelete);
    const [verifying, setVerifying] = useState(false);

    const handleVerify = async () => {
        setVerifying(true);
        await onVerify();
        setVerifying(false);
    };

    return (
        <ContentContainer>
            <Card>
                <EntityDeleteDialog
                    opened={deleteAction.states.isDialogOpened}
                    deleting={deleteAction.states.isProcessing}
                    onCancel={deleteAction.handlers.handleCancel}
                    onConfirmation={deleteAction.handlers.handleConfirm}
                    title={<LocalizedText textKey={TextKey.DeleteSenderAddress} />}
                >
                    <LocalizedText textKey={TextKey.DeleteSenderAddressDescription} substitutes={{ email }} />
                </EntityDeleteDialog>
                <Stack vertical spacing="400">
                    <Stack spacing="200" itemAlignment="center">
                        <Title level={2}>{email}</Title>
                        <Tag variant={verified ? 'success' : 'danger'}>
                            <LocalizedText textKey={verified ? TextKey.Verified : TextKey.Unverified} />
                        </Tag>
                    </Stack>
                    <Stack vertical spacing="200">
                        <Title level={3}>
                            {verified ? (
                                <LocalizedText textKey={TextKey.GuestCommunicationSentFrom} substitutes={{ email }} />
                            ) : (
                                <LocalizedText
                                    textKey={TextKey.VerifySenderAndSendGuestCommunicationFrom}
                                    substitutes={{ email }}
                                />
                            )}
                        </Title>
                        {!verified && (
                            <Body>
                                <LocalizedText textKey={TextKey.SetupDnsProvider} />
                            </Body>
                        )}
                        <Title level={3}>
                            <LocalizedText textKey={TextKey.DnsRecords} />
                        </Title>
                        <DnsRecordList records={dnsRecords} />
                        {!verified && (
                            <Body>
                                <LocalizedText textKey={TextKey.VerifyDnsSettings} />
                            </Body>
                        )}
                        <EmailSenderBanner verified={verified} verifyLoaded={verifyLoaded} />
                    </Stack>
                    <Stack distribution="space-between">
                        <Button onClick={deleteAction.handlers.handleOpen} type="submit">
                            <LocalizedText textKey={TextKey.Delete} />
                        </Button>
                        <Button variant="primary" onClick={handleVerify} disabled={verifying} type="submit">
                            <LocalizedText textKey={TextKey.Verify} />
                        </Button>
                    </Stack>
                </Stack>
            </Card>
        </ContentContainer>
    );
};

interface EmailSenderBannerProps {
    verified: boolean;
    verifyLoaded: boolean;
}

const EmailSenderBanner = ({ verified, verifyLoaded }: EmailSenderBannerProps) => {
    const localizeText = useLocalizeText();
    const couldNotVerifyDescription = localizeText({ textKey: TextKey.DnsRecordsCouldNotVerifyDescription });

    if (verified) {
        return null;
    }

    if (verifyLoaded) {
        return (
            <Banner
                variant="error"
                primaryText={<LocalizedText textKey={TextKey.DnsRecordsCouldNotVerify} />}
                secondaryText={<TextWithHelpCenterLink text={couldNotVerifyDescription} />}
            />
        );
    }

    return <Banner variant="warning" primaryText={<LocalizedText textKey={TextKey.DnsRecordsPropagationNote} />} />;
};

const linkDecorator =
    (dispatch: CommanderThunkDispatch) => (_decoratedHref: string, decoratedText: string, key: number) => (
        <Link key={key} onClick={() => dispatch(open())} inherit>
            {decoratedText}
        </Link>
    );

interface TextWithHelpCenterLinkProps {
    text: string;
}

const TextWithHelpCenterLink = ({ text }: TextWithHelpCenterLinkProps) => {
    const dispatch = useCommanderThunkDispatch();
    const localizeText = useLocalizeText();
    const links = [
        {
            id: 'helpCenter',
            text: localizeText({ textKey: TextKey.HelpCenter }),
            url: '',
        },
    ];

    return (
        <Linkify matchDecorator={matchDecorator(links)} componentDecorator={linkDecorator(dispatch)}>
            {text}
        </Linkify>
    );
};
